/**
 * Categorias → Grupos → Subgrupos.
 *
 * POR QUE ISTO É UMA TELA, e não um campo de texto no item: no sistema atual,
 * esses três níveis são texto livre digitado em cada item. Com 3 mil itens e
 * várias pessoas cadastrando, "Elétrica" e "Eletrica" passam a conviver — e aí
 * o filtro da vitrine de Pedidos mente, porque metade dos itens fica fora do
 * recorte sem ninguém perceber. Já aconteceu com Grupo escrito só com dígitos,
 * que o Sheets guardou como número e derrubou a listagem inteira.
 *
 * O custo é um passo a mais para criar um valor novo. O ganho é que renomear
 * vale para todos os itens de uma vez, e duplicata deixa de ser possível — o
 * banco recusa.
 */

import { api, ErroApi } from '../../api.js';
import { escapar } from '../../comum/formato.js';
import { avisarBloqueante } from '../../comum/aviso.js';

export interface Taxonomia {
  categorias: { id: number; nome: string; ordem: number | null; ativa: boolean; prefixo: string }[];
  grupos: { id: number; categoria_id: number | null; nome: string }[];
  subgrupos: { id: number; grupo_id: number | null; nome: string }[];
  unidades: { id: number; sigla: string }[];
}

export function carregarTaxonomia(): Promise<Taxonomia> {
  return api.get<Taxonomia>('/api/taxonomia');
}

export function montarTaxonomia(raiz: HTMLElement): void {
  raiz.innerHTML = `<p class="carregando">Carregando…</p>`;
  let termo = '';
  void desenhar();

  async function desenhar() {
    let t: Taxonomia;
    try {
      t = await carregarTaxonomia();
    } catch (erro) {
      raiz.innerHTML = `<p class="aviso">${escapar(
        erro instanceof Error ? erro.message : 'Não consegui carregar.',
      )}</p>`;
      return;
    }

    const bate = (nome: string) =>
      nome
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .includes(termo);

    // A busca corta a árvore mantendo o CAMINHO: subgrupo que bate traz o
    // grupo e a categoria dele junto — nó órfão não diz onde a pessoa está.
    const porCategoria = (id: number) =>
      t.grupos
        .filter((g) => g.categoria_id === id)
        .filter(
          (g) =>
            !termo ||
            bate(g.nome) ||
            t.subgrupos.some((s) => s.grupo_id === g.id && bate(s.nome)),
        );
    const porGrupo = (gid: number, grupoBateu: boolean) =>
      t.subgrupos
        .filter((s) => s.grupo_id === gid)
        .filter((s) => !termo || grupoBateu || bate(s.nome));
    const categorias = t.categorias.filter(
      (c) => !termo || bate(c.nome) || porCategoria(c.id).length > 0,
    );

    raiz.innerHTML = `
      <p class="dica">
        Renomear aqui vale para <b>todos os itens de uma vez</b>. Excluir não existe de
        propósito: apagar uma categoria deixaria os itens dela sem classificação, em
        silêncio.
      </p>
      <div class="barra-busca">
        <input id="tx-busca" type="search" value="${escapar(termo)}"
               placeholder="Buscar categoria, grupo ou subgrupo…" autocomplete="off">
      </div>

      ${termo && categorias.length === 0 ? `<p class="dica">Nada com "${escapar(termo)}".</p>` : ''}
      <div class="arvore">
        ${categorias
          .map(
            (c) => `
          <details class="tx-cat" open>
            <summary>
              <span class="tx-nome" data-renomear="categorias" data-id="${c.id}">${escapar(c.nome)}</span>
              <span class="tx-conta">${porCategoria(c.id).length} grupo(s)</span>
            </summary>
            <div class="tx-filhos">
              ${porCategoria(c.id)
                .map(
                  (g) => `
                <div class="tx-grupo">
                  <span class="tx-nome" data-renomear="grupos" data-id="${g.id}">${escapar(g.nome)}</span>
                  <div class="tx-filhos">
                    ${porGrupo(g.id, bate(g.nome))
                      .map(
                        (s) =>
                          `<span class="tx-sub tx-nome" data-renomear="subgrupos" data-id="${s.id}">${escapar(s.nome)}</span>`,
                      )
                      .join('')}
                    <button class="tx-add" data-novo="subgrupos" data-pai="${g.id}">+ subgrupo</button>
                  </div>
                </div>`,
                )
                .join('')}
              <button class="tx-add" data-novo="grupos" data-pai="${c.id}">+ grupo</button>
            </div>
          </details>`,
          )
          .join('')}
      </div>
      <button class="btn" data-novo="categorias">+ Nova categoria</button>
      <p class="aviso" id="tx-erro" hidden></p>`;

    // Devolve o foco e o cursor ao campo depois do redesenho — sem isso, cada
    // letra digitada "roubaria" o teclado.
    const busca = raiz.querySelector<HTMLInputElement>('#tx-busca')!;
    if (termo) {
      busca.focus();
      busca.setSelectionRange(busca.value.length, busca.value.length);
    }
  }

  let temporizador: number | undefined;
  raiz.addEventListener('input', (e) => {
    const alvo = e.target as HTMLInputElement;
    if (alvo.id !== 'tx-busca') return;
    window.clearTimeout(temporizador);
    temporizador = window.setTimeout(() => {
      termo = alvo.value
        .trim()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase();
      void desenhar();
    }, 200);
  });

  raiz.addEventListener('click', (e) => {
    const alvo = e.target as HTMLElement;

    const novo = alvo.closest<HTMLElement>('[data-novo]');
    if (novo) {
      const nivel = novo.dataset['novo']!;
      const nome = prompt(
        nivel === 'categorias'
          ? 'Nome da nova categoria:'
          : nivel === 'grupos'
            ? 'Nome do novo grupo:'
            : 'Nome do novo subgrupo:',
      );
      if (!nome?.trim()) return;
      const pai = novo.dataset['pai'];
      void criar(nivel, nome.trim(), pai ? Number(pai) : undefined);
      return;
    }

    const ren = alvo.closest<HTMLElement>('[data-renomear]');
    if (ren) {
      const atual = ren.textContent?.trim() ?? '';
      const nome = prompt(`Renomear "${atual}" para:\n\nVale para todos os itens que usam este nome.`, atual);
      if (!nome?.trim() || nome.trim() === atual) return;
      void renomear(ren.dataset['renomear']!, Number(ren.dataset['id']), nome.trim());
    }
  });

  async function criar(nivel: string, nome: string, paiId?: number) {
    try {
      await api.post(`/api/${nivel}`, paiId ? { nome, paiId } : { nome });
      await desenhar();
    } catch (erro) {
      erroNaTela(erro);
    }
  }

  async function renomear(nivel: string, id: number, nome: string) {
    try {
      await api.patch(`/api/${nivel}/${id}`, { nome });
      await desenhar();
    } catch (erro) {
      erroNaTela(erro);
    }
  }

  function erroNaTela(erro: unknown) {
    const el = raiz.querySelector<HTMLElement>('#tx-erro');
    const msg = erro instanceof ErroApi ? erro.message : 'Não consegui salvar.';
    if (el) {
      el.textContent = msg;
      el.hidden = false;
    } else {
      avisarBloqueante(msg);
    }
  }
}
